import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import useApiCall from 'hooks/use-api-call';
import { AdminApi } from 'api/apis';
import LoadingRing from 'ui/atoms/loading-ring';
import { AdminGroup, AdminOrganizationDetails } from 'api/models';
import BoxedContent from 'src/ui/molecules/boxed-content';
import CardItem from 'src/ui/atoms/card-item';
import DateAtom from 'src/ui/atoms/date';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Button from 'src/ui/atoms/button';
import Link from 'core/routing/link';
import DeleteGroupConfirmationModal from './confirm-delete-group-modal';
import DeleteGroupSuccessModal from './success-delete-group-modal';
import useGoTo from 'src/hooks/use-go-to';
import { ISSUER_ROUTES } from '../routes.config';
import Header from 'src/ui/atoms/header';
import Section from 'src/ui/atoms/section';
import AddGroupUserModal from './add-group-user-modal';
import { currentUserHasManageEnvironmentPermission } from '../../helpers/userPermissions';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import GroupPermissionsTable from './permissions-table';
import DeletePermissionSuccessModal from '../user-details/delete-permission-success-modal';
import AddPermissionModal from './add-permission-modal';
import { currentOrganizationHasManageOrganizationPermission } from '../../helpers/organizationPermissions';
import UsersTableWrapper from '../../shared/users-table';
import PageWrapper from 'src/core/layout/content/page-wrapper';

interface IssuerGroupDetailsPageProps {
  groupId: string;
}

const IssuerGroupDetailsPage: FunctionComponent<IssuerGroupDetailsPageProps> = ({ groupId }) => {
  const [group, setGroup] = useState<AdminGroup>();
  const [isConfirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [isSuccessDeleteModalOpen, setSuccessDeleteModalOpen] = useState(false);
  const [isAddGroupUserModalOpen, setAddGroupUserModalOpen] = useState(false);
  const [forceGroupUsersDataLoad, setForceGroupUsersDataLoad] = useState(false);
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);
  const [forceDataLoad, setForceDataLoad] = useState(false);
  const [isDeletePermissionSuccessModalOpen, setDeletePermissionSuccessModalOpen] = useState(false);
  const [isAddPermissionModalOpen, setAddPermissionModalOpen] = useState(false);
  const [groupOrganization, seGroupOrganization] = useState<AdminOrganizationDetails | null>(null);
  const [canSeeGroupPermissions, setCanSeeGroupPermissions] = useState(true);

  const { withApi, makeAuthenticatedApi } = useApiCall();

  const { currentUser } = useCurrentUserSelector();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const goToPermissionsPage = useGoTo(ISSUER_ROUTES.permissions);

  const fetchGroupOrganization = useCallback(() => {
    withApi(async () => {
      const organization = await adminApi.adminOrganizationsRetrieve({
        id: group?.organizationId!,
      });
      seGroupOrganization(organization);
    });
  }, [group?.organizationId]);

  const fetchGroupDetails = useCallback(() => {
    withApi(async () => {
      const groupDetails = await adminApi.adminGroupsRetrieve({
        id: groupId,
      });

      setGroup(groupDetails);
    });
  }, [groupId]);

  useEffect(() => {
    fetchGroupDetails();
  }, [withApi, adminApi, groupId]);

  useEffect(() => {
    if (groupOrganization) {
      setCanSeeGroupPermissions(
        currentOrganizationHasManageOrganizationPermission(groupOrganization) ||
          currentUserHasManageEnvironmentPermission(currentUser?.permissions || []),
      );
    }
  }, [JSON.stringify(groupOrganization)]);

  useEffect(() => {
    if (group?.organizationId) {
      fetchGroupOrganization();
    }
  }, [withApi, adminApi, group?.organizationId]);

  const deleteGroup = useCallback(() => {
    withApi(async () => {
      await adminApi.adminGroupsDestroy({
        id: groupId,
      });
      setConfirmDeleteModalOpen(false);
      setSuccessDeleteModalOpen(true);
    });
  }, [withApi, adminApi, groupId]);

  const confirmDeleteGroup = useCallback(() => {
    setConfirmDeleteModalOpen(true);
  }, []);

  if (!group) return <LoadingRing />;

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <Section>
        <WideContent
          title={<Translate name="issuerPermissionsGroupDetails.title" args={[group.name]} />}
          titleSpacing="larger"
        >
          <BoxedContent releaseSpaceWhenTitleIsEmpty={true} title={<Translate name="common.generalInformation" />}>
            <CardItem title={<Translate name="issuerPermissionsGroupDetails.groupId" />} content={group.id} />
            <CardItem
              title={<Translate name="issuerPermissionsGroupDetails.createdAt" />}
              content={<DateAtom>{group.createdAt}</DateAtom>}
            />
            <CardItem title={<Translate name="issuerPermissionsGroupDetails.name" />} content={group.name} />
            <CardItem
              title={<Translate name="issuerPermissionsGroupDetails.organization" />}
              content={group.organizationName}
            />
            <ActionButtons spacingTop="regular">
              <Button variant="secondary" onClick={confirmDeleteGroup}>
                <Translate name="issuerPermissionsGroupDetails.deleteButton" />
              </Button>
            </ActionButtons>
          </BoxedContent>
        </WideContent>
      </Section>
      <Section>
        <WideContent
          headerPanel={
            <Button variant="primary" icon="plus" iconColor="inherit" onClick={() => setAddGroupUserModalOpen(true)}>
              <Translate name="issuerGroupUsersTable.addButton" />
            </Button>
          }
        >
          <Header size="small">
            <Translate name="issuerGroupUsersTable.title" />
          </Header>
          <UsersTableWrapper
            groupId={groupId}
            groupName={group.name}
            forceDataLoad={forceGroupUsersDataLoad}
            setForceDataLoad={setForceGroupUsersDataLoad}
            hasPermission={canSeeGroupPermissions}
            showDeleteButton={true}
          />
        </WideContent>
      </Section>
      {((groupOrganization && currentOrganizationHasManageOrganizationPermission(groupOrganization)) ||
        currentUserHasManageEnvironmentPermission(currentUser?.permissions || [])) && (
        <WideContent
          headerPanel={
            ((groupOrganization && currentOrganizationHasManageOrganizationPermission(groupOrganization)) ||
              currentUserHasManageEnvironmentPermission(currentUser?.permissions || [])) && (
              <Button variant="primary" icon="plus" iconColor="inherit" onClick={() => setAddPermissionModalOpen(true)}>
                <Translate name="issuerPermissionsTable.addPermissionButton" />
              </Button>
            )
          }
        >
          <Header size="small">
            <Translate name="groupPermissionsTable.title" />
          </Header>
          <GroupPermissionsTable
            group={group}
            groupId={groupId}
            forceDataLoad={forceDataLoad}
            setForceDataLoad={setForceDataLoad}
            setDeletePermissionSuccessModalOpen={setDeletePermissionSuccessModalOpen}
            skipFilterUrlSync={true}
            hasPermission={canSeeGroupPermissions}
            groupOrganization={groupOrganization}
            showDeleteButton={true}
            setFilterPopupHeight={setFilterPopupHeight}
          />
          <ActionButtons align="start">
            <Link icon="arrow-left" to="/dashboard/permissions">
              <Translate as="span" name="issuerPermissionsUserDetails.backToPermissions" />
            </Link>
          </ActionButtons>
        </WideContent>
      )}
      {isConfirmDeleteModalOpen && (
        <DeleteGroupConfirmationModal
          group={group.name}
          onClose={() => setConfirmDeleteModalOpen(false)}
          onDelete={deleteGroup}
        />
      )}
      {isSuccessDeleteModalOpen && (
        <DeleteGroupSuccessModal
          onClose={() => {
            setSuccessDeleteModalOpen(false);
            goToPermissionsPage();
          }}
        />
      )}
      {isAddGroupUserModalOpen && (
        <AddGroupUserModal
          groupId={groupId}
          onSaveUser={() => {
            setForceGroupUsersDataLoad(true);
            setAddGroupUserModalOpen(false);
          }}
          onClose={() => setAddGroupUserModalOpen(false)}
        />
      )}
      {isAddPermissionModalOpen && (
        <AddPermissionModal
          group={group}
          groupOrganization={groupOrganization}
          onAddGroupPermission={() => {
            setForceDataLoad(true);
            setAddPermissionModalOpen(false);
          }}
          onClose={() => setAddPermissionModalOpen(false)}
        />
      )}
      {isDeletePermissionSuccessModalOpen && (
        <DeletePermissionSuccessModal onClose={() => setDeletePermissionSuccessModalOpen(false)} />
      )}
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerGroupDetailsPage, AppType.ISSUER);
